import { fetchAuthSession } from 'aws-amplify/auth';
import mockDataService from './mockDataService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://0uh435s1v7.execute-api.us-east-1.amazonaws.com/prod';

// Check if we should use mock data
const shouldUseMockData = () => {
  // STRICT PRODUCTION CHECK: Never use mock data in production
  const isProduction = process.env.NODE_ENV === 'production' || 
                      (typeof window !== 'undefined' && (
                        window.location.hostname === 'getcertifiable.com' ||
                        window.location.hostname === 'www.certifiable.me' ||
                        window.location.hostname.includes('amplifyapp.com')
                      ));
  
  if (isProduction) {
    return false;
  }
  
  // Force mock data if emergency access is being used
  if (typeof window !== 'undefined' && localStorage.getItem('force_mock_data') === 'true') {
    return true;
  }
  
  return process.env.REACT_APP_FORCE_MOCK_DATA === 'true' || 
         (process.env.NODE_ENV === 'development' && !API_BASE_URL);
};

// Function to get authenticated headers
const getAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Authorization header if we have tokens
    if (session.tokens?.idToken) {
      headers['Authorization'] = `Bearer ${session.tokens.idToken.toString()}`;
    }
    
    return headers;
  } catch (error) {
    return {
      'Content-Type': 'application/json',
    };
  }
};

// API request wrapper with error handling
const apiRequest = async (url, options = {}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export const apiUserService = {
  // Get user by ID
  async getUser(userId) {
    if (shouldUseMockData()) {
      return mockDataService.getUser(userId);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      const user = await apiRequest(`${API_BASE_URL}/users/${userId}`);
      return user;
    } catch (error) {
      if (error.message.includes('404')) {
        return null; // User not found
      }
      throw error;
    }
  },

  // Create user
  async createUser(userData) {
    if (shouldUseMockData()) {
      return mockDataService.createUser(userData);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // CRITICAL: Validate required fields before API call
    if (!userData.email || userData.email.trim() === '') {
      console.error('❌ CRITICAL: Attempting to create user without email:', userData.userId);
      throw new Error('Email is required for user creation');
    }

    if (!userData.userId || userData.userId.trim() === '') {
      console.error('❌ CRITICAL: Attempting to create user without userId');
      throw new Error('User ID is required for user creation');
    }


    const user = await apiRequest(`${API_BASE_URL}/users/${userData.userId}`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    return user;
  },

  // Update user
  async updateUser(userId, updates) {
    if (shouldUseMockData()) {
      return mockDataService.updateUser(userId, updates);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // For updates, we don't require email if it's not being changed
    if (!userId || userId.trim() === '') {
      console.error('❌ CRITICAL: Attempting to update user without userId');
      throw new Error('User ID is required for user updates');
    }


    const user = await apiRequest(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    return user;
  },

  // Save user profile (handles both create and update)
  async saveUserProfile(userData) {
    if (shouldUseMockData()) {
      return mockDataService.saveUserProfile ? mockDataService.saveUserProfile(userData) : mockDataService.createUser(userData);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // Check if this is a profile update (no email) or new user creation (requires email)
    const isProfileUpdate = !userData.email && userData.userId;
    
    if (isProfileUpdate) {
      return this.updateUser(userData.userId, userData);
    } else {
      return this.createUser(userData);
    }
  },

  // Get all users (admin function)
  async getAllUsers() {
    if (shouldUseMockData()) {
      return mockDataService.getAllUsers();
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      const response = await apiRequest(`${API_BASE_URL}/users`);
      return response;
    } catch (error) {
      console.error('Failed to get all users from API:', error);
      throw error;
    }
  },

  // Delete user
  async deleteUser(userId) {
    if (shouldUseMockData()) {
      return mockDataService.deleteUser(userId);
    }

    // Note: Delete endpoint would need to be added to API Gateway
    // For now, this is a placeholder
    throw new Error('User deletion via API not implemented yet');
  },

  // Batch operations (for admin functions)
  async batchGetUsers(userIds) {
    if (shouldUseMockData()) {
      return mockDataService.batchGetUsers(userIds);
    }

    // For now, make individual requests
    const users = await Promise.all(
      userIds.map(async (userId) => {
        try {
          return await this.getUser(userId);
        } catch (error) {
          return null;
        }
      })
    );

    return users.filter(user => user !== null);
  },

  // Search users (admin function)
  async searchUsers(searchTerm) {
    if (shouldUseMockData()) {
      return mockDataService.searchUsers(searchTerm);
    }

    // Note: Search endpoint would need to be added to API Gateway
    return mockDataService.searchUsers(searchTerm);
  }
};

export default apiUserService;
